import { Button, Card, DatePicker, Form, Grid, Input, Message, Modal, Select, Space, Tabs } from "@arco-design/web-react"
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ItemReceiptAPI, VendorAPI, POAPI, BillAPI } from "@/apis/purchasing";
import { ItemAPI } from "@/apis/material";
import { Currency, InputNumber } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import { TermsDef } from "@/enums/common";
import { ReferenceTypeDef } from "@/enums/purchasing";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { formatDate, today, TermUtil, decimalEquals } from "@/utils";
import AccountSelect from "@/components/AccountSelect";
import VendorSelect from "@/components/VendorSelect";
import _ from "lodash";
import Decimal from "decimal.js";

const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;
const TextArea = Input.TextArea;
const TabPane = Tabs.TabPane;

export const BillAdd = () => {
    const navigate = useNavigate();
    const params = useParams();
    const receiptId = params.receiptId;
    const [form] = Form.useForm();
    const [poList, setPOList] = useState();
    const [totalExpenses, setTotalExpenses] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [items, setItems] = useState([]);
    const [receiptItemQuantityAndAmount, setReceiptItemQuantityAndAmount] = useState([]);
    const [bill, setBill] = useState();

    const getPOList = useCallback(async () => {
        if (!receiptId) {
            const unbilledPOs = await POAPI.getNotBilled();
            setPOList(unbilledPOs);
        }
    }, [receiptId])

    const onVendorChanged = async (val) => {
        const vendor = await VendorAPI.get(val);
        form.setFieldValue("vendorAddress", `${vendor.billingAddress}, ${vendor.billingCity}, ${vendor.billingState} ${vendor.billingZip}`);
        if (vendor.term) {
            const billDate = form.getFieldValue("billDate");
            const dueDate = TermUtil.dueDate(billDate, vendor.term);
            form.setFieldValue("term", vendor.term);
            form.setFieldValue("dueDate", formatDate(dueDate));
        }
    }

    const onReceiptChanged = useCallback(async (receiptId) => {
        var items = [];
        if (receiptId) {
            const receipt = await ItemReceiptAPI.get(receiptId);
            form.setFieldValue("receiptNo", receipt.receiptNo);
            form.setFieldValue("poNo", receipt.poNo);

            const grouped = _.chain(receipt.items)
                .groupBy("itemId")
                .map((value, key) => {
                    let totalQty = new Decimal(0);
                    let totalAmount = new Decimal(0);
                    for (const index in value) {
                        const item = value[index];
                        const itemQty = new Decimal(item.quantity);
                        const itemAmount = new Decimal(item.amount);
                        totalQty = totalQty.plus(itemQty);
                        totalAmount = totalAmount.plus(itemAmount);
                    }
                    return {
                        key,
                        quantity: totalQty,
                        amount: totalAmount
                    }
                })
                .value();
            setReceiptItemQuantityAndAmount(grouped);

            items = _.concat(items, receipt.items);
        } else {
            const nonInventoryItems = await ItemAPI.getNonInventoryItems();
            items = _.concat(items, _.map(nonInventoryItems, (item) => {
                return {
                    itemId: item.id,
                    itemName: item.itemName,
                    itemNo: item.itemNo,
                    uom: item.uom,
                    rate: item.stdCost,
                    description: item.description
                }
            }));
        }

        setItems(items);
    }, [form]);

    const onItemChanged = async (index, val) => {
        const vendorId = form.getFieldValue("vendorId");
        const billItem = await BillAPI.getVendorLastItem(vendorId, val);
        const item = _.find(items, o => o.itemId === val);
        if (item) {
            form.setFieldValue(`items[${index}].description`, item.description);
            form.setFieldValue(`items[${index}].uom`, item.uom);
            form.setFieldValue(`items[${index}].cost`, billItem ? billItem.cost : item.rate);
        }
    }

    const calcDueDate = () => {
        const date = form.getFieldValue("billDate");
        const term = form.getFieldValue("term");

        form.setFieldValue("dueDate", formatDate(TermUtil.dueDate(date, term)));
    }

    const calcTotalExpense = () => {
        const expenseItems = form.getFieldValue("expenses");
        let totalExpensesAmount = new Decimal(0);
        for (const index in expenseItems) {
            const expense = expenseItems[index];
            const amount = new Decimal(expense.amount);
            totalExpensesAmount = totalExpensesAmount.plus(amount);
        }
        setTotalExpenses(totalExpensesAmount);
    }

    const calcItemAmount = (index) => {
        const updateItem = form.getFieldValue(`items[${index}]`);
        const quantity = new Decimal(updateItem.quantity);
        const cost = new Decimal(updateItem.cost);
        const amount = quantity.mul(cost);
        form.setFieldValue(`items[${index}].amount`, amount);
        const items = form.getFieldValue("items");

        let totalItemsAmount = new Decimal(0);
        for (const index in items) {
            const item = items[index];
            const amount = new Decimal(item.amount);
            totalItemsAmount = totalItemsAmount.plus(amount);
        }
        setTotalItems(totalItemsAmount);
    }

    const calcTotalAmount = useCallback(() => {
        const itemAmount = new Decimal(totalItems);
        const expenseAmount = new Decimal(totalExpenses);
        const total = itemAmount.plus(expenseAmount);
        form.setFieldValue("totalAmount", total);
    }, [form, totalItems, totalExpenses]);

    const existSameKeyBill = async (billForm) => {
        if (billForm.billKey === null || billForm.billKey === undefined || billForm.billKey === "") {
            return false;
        }
        const bills = await BillAPI.query({ vendor: billForm.vendorId, billKey: billForm.billKey });
        if (bills.total > 0) {
            return true;
        }

        return false;
    }

    const save = async () => {
        try {
            const values = await form.validate();

            if (values.receiptId) {
                const grouped = _.chain(values.items)
                    .groupBy("itemId")
                    .map((value, key) => {
                        let totalQty = new Decimal(0);
                        let totalAmount = new Decimal(0);
                        for (const index in value) {
                            const item = value[index];
                            const itemQty = new Decimal(item.quantity);
                            const itemAmount = new Decimal(item.amount);
                            totalQty = totalQty.plus(itemQty);
                            totalAmount = totalAmount.plus(itemAmount);
                        }
                        return {
                            key,
                            quantity: totalQty,
                            amount: totalAmount
                        }
                    })
                    .value();

                _.forEach(grouped, o => {
                    const item = _.find(receiptItemQuantityAndAmount, item => item.key === o.key);
                    if (!decimalEquals(item.quantity, o.quantity) || !decimalEquals(item.amount, o.amount)) {
                        throw new Error("The total Qty AND Amount of each item needs to equal the Qty and Amount from the item receipt.");
                    }
                });
            }

            if (await existSameKeyBill(values)) {
                Modal.warning({
                    title: "Warning",
                    content: "Possible duplicate bill!",
                    onOk: async () => {
                        createBill(values);
                    }
                });
            } else {
                await createBill(values);
            }
        } catch (error) {
            Message.error(error.message);
        }
    }

    const createBill = async (values) => {
        try {
            const billId = await BillAPI.add(values);
            Message.success("Bill created successfully");
            navigate("/workspace/purchasing/bills/" + billId);
        } catch (error) {
            Message.error(error.message);
        }
    }

    const load = useCallback(async () => {
        if (receiptId) {
            const receipt = await ItemReceiptAPI.get(receiptId);
            const vendor = await VendorAPI.get(receipt.vendorId);

            const values = {
                vendorId: receipt.vendorId,
                vendorAddress: vendor.billingAddress,
                billDate: receipt.receiveDate,
                receiptId: receipt.id,
                receiptNo: receipt.receiptNo,
                poNo: receipt.poNo,
                term: vendor.term ?? 1,
                reference: receipt.reference || []
            };
            values.dueDate = formatDate(TermUtil.dueDate(values.billDate, values.term));

            let billItems = [];
            for (const index in receipt.items) {
                const item = receipt.items[index];
                const found = _.find(billItems, o => o.itemId === item.itemId && o.cost === item.rate);
                if (found) {
                    const itemQty = new Decimal(item.quantity);
                    found.quantity = itemQty.plus(found.quantity);
                    const itemAmount = new Decimal(item.amount);
                    found.amount = itemAmount.plus(found.amount);
                } else {
                    billItems.push({
                        key: billItems.length,
                        itemId: item.itemId,
                        description: item.description,
                        quantity: item.quantity,
                        uom: item.uom,
                        cost: item.rate,
                        amount: item.amount
                    });
                }
            }

            values.items = billItems;
            setBill(values);

            onReceiptChanged(receiptId);
            form.setFieldsValue(values);

            let totalItemsAmount = new Decimal(0);
            for (const index in values.items) {
                const item = values.items[index];
                const amount = new Decimal(item.amount);
                totalItemsAmount = totalItemsAmount.plus(amount);
            }
            setTotalItems(totalItemsAmount);
            let totalExpensesAmount = new Decimal(0);
            for (const index in values.expenses) {
                const expense = values.expenses[index];
                const amount = new Decimal(expense.amount);
                totalExpensesAmount = totalExpensesAmount.plus(amount);
            }
            setTotalExpenses(totalExpensesAmount);
        } else {
            setBill({
                billDate: today(),
            });

            onReceiptChanged(null);
            form.setFieldsValue({ billDate: today() });
        }
    }, [receiptId, onReceiptChanged, form]);

    useEffect(() => {
        getPOList();
    }, [getPOList]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        calcTotalAmount();
    }, [calcTotalAmount]);

    return (
        <Card title="Add New Bill" extra={
            <Space>
                <Button type="primary" onClick={() => save()}>Save</Button>
                <Link to="/workspace/purchasing/bills">
                    <Button>Back</Button>
                </Link>
            </Space>
        }>
            {bill &&
                <Form form={form} colon
                    labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                    <FormItem field="id" hidden>
                        <Input readOnly />
                    </FormItem>
                    <Row gutter={12}>
                        <Col span={10}>
                            <FormItem label="Vendor" field="vendorId" required rules={[{ required: true }]}>
                                <VendorSelect onChange={(val) => onVendorChanged(val)} />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Bill Date" field="billDate" required rules={[{ required: true }]}
                                labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                <DatePicker onChange={() => calcDueDate()} />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem label="Paid Date" field="processDate"
                                labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <DatePicker />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={10}>
                            <FormItem label="Address" field="vendorAddress">
                                <Input />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Item Receipt#" field="receiptId"
                                hidden>
                                <Input readOnly />
                            </FormItem>
                            <FormItem label="Item Receipt#" field="receiptNo"
                                hidden={bill.receiptId == null}
                                labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                <Input readOnly />
                            </FormItem>
                            {bill.receiptId == null
                                ? <FormItem label="PO#" field="poId"
                                    labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                    <Select options={_.map(poList, (item) => ({ value: item.id, label: item.poNo }))} allowClear />
                                </FormItem>
                                : <FormItem label="PO#" field="poNo"
                                    labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                    <Input readOnly />
                                </FormItem>
                            }
                        </Col>
                        <Col span={6}>
                            <FormItem label="Invoice#" field="invoiceNo" required rules={[{ required: true }]}
                                labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Input />
                            </FormItem>
                            <FormItem label="Key" field="billKey"
                                labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Input />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={10}>
                            <FormItem label="Reference#" field="reference">
                                <Form.List field="reference">
                                    {(fields, { add, remove, move }) =>
                                        <>
                                            {fields.map((item, index) => {
                                                return (
                                                    <FormItem key={item.key} style={{ marginBottom: 0 }}>
                                                        <Space className="form-item-row">
                                                            <FormItem field={item.field + ".type"}>
                                                                <EnumSelect enumObj={ReferenceTypeDef} order="label" style={{ width: 200 }} showSearch />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".number"}>
                                                                <Input />
                                                            </FormItem>
                                                            <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)}></Button>
                                                        </Space>
                                                    </FormItem>
                                                )
                                            })}
                                            <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Reference</Button>
                                        </>
                                    }
                                </Form.List>
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Terms" field="term" required rules={[{ required: true }]}
                                labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                <EnumSelect enumObj={TermsDef} onChange={(val) => {
                                    calcDueDate();
                                }} />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem label="Due Date" field="dueDate" required rules={[{ required: true }]}
                                labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <DatePicker />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={18}>
                            <FormItem label="Memo" field="memo" labelCol={{ span: 3 }} wrapperCol={{ span: 21 }}>
                                <TextArea />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem label="Total Amount" field="totalAmount"
                                labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <InputNumber readOnly />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Tabs defaultActiveTab="2" type="card-gutter" lazyload={false}>
                                <TabPane key="1" title={
                                    <Space size={80}>
                                        <span>Expenses</span>
                                        <Currency value={totalExpenses} />
                                    </Space>
                                }>
                                    <Form.List field="expenses" >
                                        {(fields, { add, remove, move }) =>
                                            <div style={{ marginLeft: 10, marginRight: 10 }}>
                                                <table width="100%">
                                                    <thead>
                                                        <tr>
                                                            <th width={280}>Account</th>
                                                            <th width={200}>Amount</th>
                                                            <th>Memo</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {fields.map((item, index) => {
                                                            return (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.id`} hidden>
                                                                            <Input />
                                                                        </FormItem>
                                                                        <FormItem field={`${item.field}.accountId`} noStyle required rules={[{ required: true }]}>
                                                                            <AccountSelect category={2} showSearch />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.amount`} noStyle required rules={[{ required: true }]}>
                                                                            <InputNumber onChange={(val) => calcTotalExpense()} />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.memo`} noStyle required rules={[{ required: true }]}>
                                                                            <Input />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem noStyle>
                                                                            <Button type="text" status="danger" icon={<IconDelete />}
                                                                                onClick={() => {
                                                                                    remove(index);
                                                                                    calcTotalExpense();
                                                                                }}></Button>
                                                                        </FormItem>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                </table>
                                                <FormItem>
                                                    <Button type="text" icon={<IconPlus />} onClick={() => add()} size="mini">Add Expense</Button>
                                                </FormItem>
                                            </div>
                                        }
                                    </Form.List>
                                </TabPane>
                                <TabPane key="2" title={
                                    <Space size={80}>
                                        <span>Items</span>
                                        <Currency value={totalItems} />
                                    </Space>
                                }>
                                    <Form.List field="items" >
                                        {(fields, { add, remove, move }) =>
                                            <div style={{ marginLeft: 10, marginRight: 10 }}>
                                                <table width="100%">
                                                    <thead>
                                                        <tr>
                                                            <th>Item</th>
                                                            <th>Description</th>
                                                            <th width={120}>Qty</th>
                                                            <th width={80}>U/M</th>
                                                            <th width={120}>Cost</th>
                                                            <th width={150}>Amount</th>
                                                            <td width={20}></td>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {fields.map((item, index) => {
                                                            return (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.itemId`} noStyle required rules={[{ required: true }]}>
                                                                            <Select showSearch options={_.map(items, (item) => ({ value: item.itemId, label: item.itemName }))} onChange={(val) => onItemChanged(index, val)} />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.description`} noStyle>
                                                                            <Input />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.quantity`} noStyle required rules={[{ required: true }]}>
                                                                            <InputNumber onChange={() => calcItemAmount(index)} />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.uom`} noStyle>
                                                                            <Input readOnly />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.cost`} noStyle required rules={[{ required: true }]}>
                                                                            <InputNumber onChange={() => calcItemAmount(index)} />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem field={`${item.field}.amount`} noStyle>
                                                                            <InputNumber readOnly />
                                                                        </FormItem>
                                                                    </td>
                                                                    <td>
                                                                        <FormItem noStyle>
                                                                            <Button type="text" status="danger" icon={<IconDelete />}
                                                                                onClick={() => {
                                                                                    remove(index);
                                                                                    calcItemAmount(index);
                                                                                }}></Button>
                                                                        </FormItem>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                </table>
                                                <FormItem>
                                                    <Button type="text" icon={<IconPlus />} onClick={() => add()} size="mini">Add Item</Button>
                                                </FormItem>
                                            </div>
                                        }
                                    </Form.List>
                                </TabPane>
                            </Tabs>
                        </Col>
                    </Row>
                </Form>
            }
        </Card>
    )
}
